import React from 'react';
import { Globe } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Region picker for the meta pages.
 *
 * Renders nothing when only one region has data, so a single-region deployment
 * looks exactly as it did before regions existed.
 *
 * Region names come from the pipeline as canonical English keys ("Japan", "USA",
 * "Korea"); `regions.<key>` translates them where a locale provides it, and the
 * key itself is a perfectly readable fallback.
 */
export default function RegionSwitcher({
  region,
  regions,
  onChange,
  className = '',
  variant = 'light',
}) {
  const { t } = useLanguage();
  if (!regions || regions.length < 2) return null;

  const label = t('meta.regionLabel');
  // t() returns the key itself when a translation is missing, so compare against
  // the key rather than testing for falsiness.
  const labelText = label === 'meta.regionLabel' ? 'Region' : label;

  const onDark = variant === 'dark';

  return (
    <label className={`flex items-center gap-2 ${className}`}>
      <span className="sr-only">{labelText}</span>
      <Globe
        aria-hidden="true"
        className={`h-4 w-4 ${onDark ? 'text-white/80' : 'text-muted-foreground'}`}
      />
      <select
        aria-label={labelText}
        className={
          onDark
            ? 'h-9 rounded-md border border-white/30 bg-white/10 px-3 text-sm text-white [&>option]:text-foreground'
            : 'h-9 rounded-md border border-input bg-background px-3 text-sm'
        }
        value={region || ''}
        onChange={(event) => onChange(event.target.value)}
      >
        {regions.map((entry) => {
          const translated = t(`regions.${entry.region}`);
          const name = translated === `regions.${entry.region}` ? entry.region : translated;
          return (
            <option key={entry.region} value={entry.region}>
              {name}
            </option>
          );
        })}
      </select>
    </label>
  );
}
